import { useUser, SignIn, SignUp } from '@clerk/clerk-react'
import { Center, Text, Button, Modal, useMantineColorScheme } from '@mantine/core'
import { useState, useEffect } from 'react'
import { IconMapPin } from '@tabler/icons-react'

interface ProtectedActionProps {
    children: React.ReactNode
    fallbackAction?: () => void
    fallbackText?: string
    fallbackIcon?: React.ReactNode
    fallbackVariant?: 'filled' | 'outline' | 'light' | 'subtle' | 'default'
    fallbackSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
    fallbackFullWidth?: boolean
    fallbackColor?: string
}

export function ProtectedAction({
    children,
    fallbackAction,
    fallbackText = "Sign in to create spottings",
    fallbackIcon = <IconMapPin size={16} />,
    fallbackVariant = "filled",
    fallbackSize = "md",
    fallbackFullWidth = true,
    fallbackColor
}: ProtectedActionProps) {
    const { isLoaded, isSignedIn } = useUser()
    const { colorScheme } = useMantineColorScheme()
    const [showAuth, setShowAuth] = useState(false)
    const [authMode, setAuthMode] = useState<'sign-in' | 'sign-up'>('sign-in')

    // Close modal when user signs in
    const handleAuthSuccess = () => {
        setShowAuth(false)
    }

    // Close modal when user successfully signs in
    useEffect(() => {
        if (isSignedIn && showAuth) {
            handleAuthSuccess()
        }
    }, [isSignedIn, showAuth])

    // Clerk appearance configuration with dark mode support
    const getClerkAppearance = () => ({
        variables: {
            colorPrimary: colorScheme === 'dark' ? '#ff4d4d' : '#e60000',
            colorBackground: colorScheme === 'dark' ? '#1a1b1e' : '#ffffff',
            colorText: colorScheme === 'dark' ? '#ffffff' : '#000000',
            colorTextSecondary: colorScheme === 'dark' ? '#a6a7ab' : '#6c757d',
            colorInputBackground: colorScheme === 'dark' ? '#25262b' : '#ffffff',
            colorInputText: colorScheme === 'dark' ? '#ffffff' : '#000000',
            borderRadius: '8px',
            fontFamily: 'Inter, system-ui, sans-serif',
        },
        elements: {
            rootBox: {
                width: '100%',
                minHeight: '300px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            },
            card: {
                boxShadow: 'none',
                border: 'none',
                borderRadius: '0',
                padding: '0',
                margin: '0 auto',
                background: 'transparent',
                backgroundColor: 'transparent',
                minHeight: '300px',
                width: '100%',
                maxWidth: '400px',
            },
            cardBox: {
                boxShadow: 'none',
                border: 'none',
                borderRadius: '0',
                background: 'transparent',
                backgroundColor: 'transparent',
                width: '100%',
            },
            main: {
                background: 'transparent',
                backgroundColor: 'transparent',
                width: '100%',
            },
            headerTitle: {
                display: 'none',
            },
            headerSubtitle: {
                display: 'none',
            },
            formButtonPrimary: {
                backgroundColor: colorScheme === 'dark' ? '#ff4d4d' : '#e60000',
                color: '#ffffff',
                '&:hover': {
                    backgroundColor: colorScheme === 'dark' ? '#ff1a1a' : '#cc0000',
                },
                '&:focus': {
                    backgroundColor: colorScheme === 'dark' ? '#ff1a1a' : '#cc0000',
                    boxShadow: colorScheme === 'dark'
                        ? '0 0 0 2px rgba(255, 77, 77, 0.2)'
                        : '0 0 0 2px rgba(230, 0, 0, 0.2)',
                },
            },
            formFieldInput: {
                backgroundColor: colorScheme === 'dark' ? '#25262b' : '#ffffff',
                borderColor: colorScheme === 'dark' ? '#373a40' : '#e9ecef',
                color: colorScheme === 'dark' ? '#ffffff' : '#000000',
                '&:focus': {
                    borderColor: colorScheme === 'dark' ? '#ff4d4d' : '#e60000',
                    boxShadow: colorScheme === 'dark'
                        ? '0 0 0 1px rgba(255, 77, 77, 0.2)'
                        : '0 0 0 1px rgba(230, 0, 0, 0.2)',
                },
                '&::placeholder': {
                    color: colorScheme === 'dark' ? '#a6a7ab' : '#6c757d',
                },
            },
            formFieldLabel: {
                color: colorScheme === 'dark' ? '#ffffff' : '#000000',
                fontWeight: '500',
            },
            identityPreviewText: {
                color: colorScheme === 'dark' ? '#a6a7ab' : '#6c757d',
            },
            identityPreviewEditButton: {
                color: colorScheme === 'dark' ? '#ff4d4d' : '#e60000',
            },
            formFieldSuccessText: {
                color: colorScheme === 'dark' ? '#51cf66' : '#37b24d',
            },
            formFieldErrorText: {
                color: colorScheme === 'dark' ? '#ff6b6b' : '#e03131',
            },
            footerActionLink: {
                display: 'none',
            },
            dividerLine: {
                backgroundColor: colorScheme === 'dark' ? '#373a40' : '#e9ecef',
            },
            dividerText: {
                color: colorScheme === 'dark' ? '#a6a7ab' : '#6c757d',
            },
            socialButtonsBlockButton: {
                backgroundColor: colorScheme === 'dark' ? '#25262b' : '#ffffff',
                borderColor: colorScheme === 'dark' ? '#373a40' : '#e9ecef',
                color: colorScheme === 'dark' ? '#ffffff' : '#000000',
                '&:hover': {
                    backgroundColor: colorScheme === 'dark' ? '#2c2e33' : '#f8f9fa',
                },
            },
            otpCodeFieldInput: {
                backgroundColor: colorScheme === 'dark' ? '#25262b' : '#ffffff',
                borderColor: colorScheme === 'dark' ? '#373a40' : '#e9ecef',
                color: colorScheme === 'dark' ? '#ffffff' : '#000000',
                '&:focus': {
                    borderColor: colorScheme === 'dark' ? '#ff4d4d' : '#e60000',
                },
            },
        },
    })

    // Still loading
    if (!isLoaded) {
        return (
            <Button loading disabled>
                Loading...
            </Button>
        )
    }

    // User is signed in, show the protected content
    if (isSignedIn) {
        return <>{children}</>
    }

    // User is not signed in, show sign-in prompt
    return (
        <>
            <Button
                leftSection={fallbackIcon}
                onClick={() => {
                    if (fallbackAction) {
                        fallbackAction()
                    } else {
                        setShowAuth(true)
                    }
                }}
                variant={fallbackVariant}
                size={fallbackSize}
                fullWidth={fallbackFullWidth}
                style={{ touchAction: 'manipulation' }}
                color={fallbackColor}
            >
                {fallbackText}
            </Button>

            {/* Auth Modal */}
            <Modal
                opened={showAuth}
                onClose={() => setShowAuth(false)}
                closeOnClickOutside={false}
                title={authMode === 'sign-in' ? 'Sign in to Spotter' : 'Join Spotter'}
                centered
                size="lg"
                styles={{
                    body: {
                        padding: '1rem',
                        minHeight: '400px',
                    },
                    content: {
                        maxHeight: '90vh',
                        overflow: 'auto',
                        boxShadow: colorScheme === 'dark'
                            ? '0 4px 12px rgba(0, 0, 0, 0.4)'
                            : '0 4px 12px rgba(0, 0, 0, 0.15)',
                        backgroundColor: colorScheme === 'dark' ? '#1a1b1e' : '#ffffff',
                    },
                    header: {
                        paddingBottom: '1rem',
                        backgroundColor: colorScheme === 'dark' ? '#1a1b1e' : '#ffffff',
                        borderBottom: `1px solid ${colorScheme === 'dark' ? '#373a40' : '#e9ecef'}`,
                    },
                    title: {
                        color: colorScheme === 'dark' ? '#ffffff' : '#000000',
                    },
                }}
            >
                <Center style={{ minHeight: '350px' }}>
                    {authMode === 'sign-in' ? (
                        <SignIn
                            routing="virtual"
                            appearance={getClerkAppearance()}
                        />
                    ) : (
                        <SignUp
                            routing="virtual"
                            appearance={getClerkAppearance()}
                        />
                    )}
                </Center>

                <Center mt="md">
                    <Text size="sm" c="dimmed">
                        {authMode === 'sign-in' ? "Don't have an account? " : "Already have an account? "}
                        <Text
                            component="button"
                            c="blue"
                            td="underline"
                            bg="transparent"
                            style={{ border: 'none', cursor: 'pointer' }}
                            onClick={() => setAuthMode(authMode === 'sign-in' ? 'sign-up' : 'sign-in')}
                        >
                            {authMode === 'sign-in' ? 'Sign up' : 'Sign in'}
                        </Text>
                    </Text>
                </Center>
            </Modal>
        </>
    )
} 